import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { PitchingRankingStats, DataSection, ResultDataNew, ScoringData, RunnerSafe } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import RankingStatCalculatorBase from "./../RankingStatCalculatorBase.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import ClassifiedPitchesFilter from "../../../filters/contextual/ClassifiedPitchesFilter.js";
import ContextualFilterBase from "../../../filters/contextual/ContextualFilterBase.js";
import * as PlateAppearanceResultClassifiers from '../../../classificators/plateAppearanceResult/index.js';
import * as RunnerSafeTypeClassifiers from '../../../classificators/runnerSafeType/index.js';
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import resultsSliceForPitcher from "../../../classificators/resultsSlice/resultsSliceForPitcher.js";

class NewWildPitchesCalculator extends RankingStatCalculatorBase{
    constructor(
        customResultsProcessor, rankingStatCaseVerifier, numberFormatter, contextualResultFilter,
        wildPitchOnDroppedThirdStrikeClassifier, wildPitchWithRunnerAdvancingClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (typeof(wildPitchOnDroppedThirdStrikeClassifier) !== 'undefined' && typeof(wildPitchOnDroppedThirdStrikeClassifier) !== 'function') {
            throw new TypeError('wildPitchOnDroppedThirdStrikeClassifier must be an function or undefined');
        }
        if (typeof(wildPitchWithRunnerAdvancingClassifier) !== 'undefined' && typeof(wildPitchWithRunnerAdvancingClassifier) !== 'function') {
            throw new TypeError('wildPitchWithRunnerAdvancingClassifier must be an function or undefined');
        }

        super(
            [ 
                PitchingRankingStats.WildPitches
            ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    pitchingScopeContext,
                    resultsSliceForPitcher
                )
            )
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.contextualFilter = contextualResultFilter || new ClassifiedPitchesFilter(this.resultsProcessor);
        this.wildPitchOnDroppedThirdStrikeClassifier = wildPitchOnDroppedThirdStrikeClassifier || PlateAppearanceResultClassifiers.classifiesAsWildPitch;
        this.wildPitchWithRunnerAdvancingClassifier = wildPitchWithRunnerAdvancingClassifier || RunnerSafeTypeClassifiers.classifiesAsWildPitch;
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        const results = slice.getSmallestSlice();
        const pitches = this.filterResults(results, session, league);
        let wildPitches = 0;

        for (let i = 0; i < pitches.length; i++) {
            const pitch = pitches[i];

            const paResult = this.resultsProcessor.getObjectContent(
                pitch, ResultDataNew.getDataPath(ScoringData.PlateAppearanceResult.key, DataSection.Scoring.key)
            );

            // @see https://baseball.fandom.com/wiki/Wild_pitch
            // @see https://www.mlb.com/glossary/standard-stats/wild-pitch
            if (!paResult) {
                continue;
            }

            // When (a) first base is unoccupied and/or (b) there are two outs, the catcher must catch the third strike in order to retire the batter;
            // if a wild pitch occurs on such a third strike, the pitcher is credited with a strikeout,
            // but the batter is not retired—he may attempt to advance to first base and must be tagged with the ball or forced at first in order to effect an out.
            if (this.wildPitchOnDroppedThirdStrikeClassifier(paResult)) {
                wildPitches++;
                continue;
            }

            // A pitcher is charged with a wild pitch when his pitch is so errant that the catcher is unable to control it and, as a result, baserunner(s) advance.
            // (This is an important stipulation. No matter how poor the pitch, a pitcher is only charged with a WP if at least one runner moves up a base, and he cannot be charged with a wild pitch if no one is on base -- unless it allows the batter to reach base on a third strike.)
            const runnerSafe = this.resultsProcessor.getObjectContent(
                pitch, ResultDataNew.getDataPath(ScoringData.RunnerSafe.key, DataSection.Scoring.key)
            );

            if (!runnerSafe || runnerSafe.length === 0) {
                continue;
            }

            if (runnerSafe.some(rs => this.wildPitchWithRunnerAdvancingClassifier(rs[RunnerSafe.Type.key]))) {
                wildPitches++;
                continue;
            }
        }

        if (!wildPitches) {
            return null;
        }

        return RankingStat.fromParameter(
            this.supportedParams[0], this.numberFormatter.format(wildPitches), wildPitches
        );
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default NewWildPitchesCalculator;